const axios = require('axios').default;
const stringUtils = require('./stringUtils');
let API_URL = process.env.LOCATION_SERVICE_URL || process.env.REACT_APP_LOCATION_SERVICE_URL || "https://location-service.centivo.dev/";
let ACCESS_TOKEN = process.env.LOCATION_SERVICE_ACCESS_TOKEN || process.env.REACT_APP_LOCATION_SERVICE_ACCESS_TOKEN;
axios.defaults.timeout = 30000;

class LocationClient {
    constructor(apiUrl = API_URL, accessToken = ACCESS_TOKEN) {
        API_URL = apiUrl;
        ACCESS_TOKEN = accessToken;
    }

    // Location cache by ZipCode
    LOCATION_BY_ZIP_CACHE = {};

    /**
     * @param {Object} locationResult Location object returned by location-service API
     * @param {boolean} [allowPartial=false] Allow incomplete address result
     * @returns Standardized address format
     */
    generateAddressData(locationResult, allowPartial = false) {
        if (locationResult == null || (!allowPartial && (locationResult.uspsAddressComponents == null || locationResult.googleAddressComponents == null))) {
            return null;
        }
        const googleAddressComponents = locationResult.googleAddressComponents;
        let address1;
        if (googleAddressComponents != null) {
            address1 = '';
            if (googleAddressComponents.street_number != null) {
                address1 += googleAddressComponents.street_number;
            }
            if (googleAddressComponents.street_name != null) {
                address1 += ' ' + googleAddressComponents.street_name;
            }
        }

        // NOTE: we use usps address2 because google strips down subpremise to bare
        let address2;
        if (locationResult.uspsAddressComponents != null && locationResult.uspsAddressComponents.address2 != null) {
            address2 = stringUtils.camelCaseAddress2(locationResult.uspsAddressComponents.address2);
        }else if(locationResult.googleAddressComponents.subpremise){
            address2 = locationResult.googleAddressComponents.subpremise;
        }
        return {
            address1: address1 ?? null,
            address2: address2 ?? null,
            city: googleAddressComponents?.city ?? null,
            state: googleAddressComponents?.state ?? null,
            zipCode: googleAddressComponents?.postal_code ?? null,
            country: googleAddressComponents?.country ?? null
        }
    }

    /**
     * @param {Object} locationResult Location object returned by location-service API
     * @param {boolean} [allowPartial=false] Allow incomplete address result
     * @returns Standardized location format
     */
    generateFullLocation(locationResult, allowPartial = false) {
        let formattedLocation = null;
        if (locationResult != null) {
            let addressData = this.generateAddressData(locationResult, allowPartial);
            formattedLocation = {
                address: addressData,
                lat: locationResult.lat,
                long: locationResult.long,
                partial: locationResult.partial ?? true
            };
        }
        return formattedLocation;
    }

    /**
     * @param {string} addressString 
     * @returns location-servie location object
     */
    async getLocationResponse(addressString) {
        const response = await axios.get(`${API_URL}getLocation`, {
            headers: {
                'content-type': 'application/json',
                'Authorization': ACCESS_TOKEN
            },
            params: {
                addressString
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {string} address1 
     * @param {string} address2 
     * @param {string} city 
     * @param {string} state 
     * @param {string} zipCode 
     * @returns location-servie location object
     */
    async getLocationValidationResponse(address1, address2 = null, city, state, zipCode) {
        const response = await axios.get(`${API_URL}getLocationValidation`, {
            headers: {
                'content-type': 'application/json',
                'Authorization': ACCESS_TOKEN
            },
            params: {
                address1,
                address2,
                city,
                state,
                zipCode
            }
        });
        if (response.status !== 200) {
            return null;
        }
        return response.data;
    }

    /**
     * @param {string} zipCode
     * @param {boolean} [readCache=true] Check in-memory cache for result
     * @param {boolean} [writeCache=true] Save result to in-memory cache
     * @returns location-servie location object
     */
    async getLocationByZipCodeResponse(zipCode, readCache = true, writeCache = true) {
        let result = null;

        if (readCache) {
            result = this.LOCATION_BY_ZIP_CACHE[zipCode] ?? null;
        }

        if (result == null) {
            const response = await axios.get(`${API_URL}getLocationByZipCode`, {
                headers: {
                    'content-type': 'application/json',
                    'Authorization': ACCESS_TOKEN
                },
                params: {
                    zipCode
                }
            });

            if (response.status === 200) {
                result = response.data;
            }
        }

        if (writeCache && result != null) {
            this.LOCATION_BY_ZIP_CACHE[zipCode] = result;
        }

        return result;
    }

    /**
     * @param {string} zipCode
     * @param {boolean} [allowPartial=false] Allow incomplete address result
     * @param {boolean} [readCache=true] Check in-memory cache for result
     * @param {boolean} [writeCache=true] Save result to in-memory cache
     * @returns Standardized address object
     */
    async getAddressByZipCode(zipCode, allowPartial = false, readCache = true, writeCache = true) {
        let rawLocation = await this.getLocationByZipCodeResponse(zipCode, readCache, writeCache);
        return this.generateAddressData(rawLocation, allowPartial);
    }

    /**
     * @param {string} zipCode
     * @param {boolean} [allowPartial=false] Allow incomplete address result
     * @param {boolean} [readCache=true] Check in-memory cache for result
     * @param {boolean} [writeCache=true] Save result to in-memory cache
     * @returns Standardized location object
     */
    async getLocationByZipCode(zipCode, allowPartial = false, readCache = true, writeCache = true) {
        let rawLocation = await this.getLocationByZipCodeResponse(zipCode, readCache, writeCache);
        return this.generateFullLocation(rawLocation, allowPartial);
    }

    /**
     * @param {string} placeId 
     * @returns {{
     *   version: number
     *   placeIdKey: string
     *   created: string
     *   uuid: string
     *   lat: number
     *   long: number
     *   modified: string
     *   state: string
     * }}
     */
    async getGeometryByPlaceId(placeId) {
        const response = await axios.get(`${API_URL}getGeometryByPlaceId`, {
            headers: {
                'content-type': 'application/json',
                'Authorization': ACCESS_TOKEN
            },
            params: {
                place_id: placeId
            }
        })

        return response.data;
    }

    /**
     * @param {string} input 
     * @returns {{
     *   version: number
     *   created: string
     *   uuid: string
     *   modified: string
     *   autoSuggestKey: string
     *   predictions: { place_id: string, suggestion: string }[]
     * }}
     */
    async getAutoSuggestPlaces(input) {
        const response = await axios.get(`${API_URL}getAutoSuggestPlaces`, {
            headers: {
                'content-type': 'application/json',
                'Authorization': ACCESS_TOKEN
            },
            params: {
                input
            }
        });

        return response.data;
    }
    
}

module.exports = {
    LocationClient,
    locationClient: new LocationClient()
}